import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { integrationsSelector } from "Reducers/integration";
import { gitProjectSelector } from "Reducers/project/project";
import { subscriptionSelector } from "Reducers/subscription";
import useBlackFireSellableStatus from "src/organization/pages/project/containers/ProjectOverview/BlackfireCard/useBlackFireSellableStatus";
import { projectCapabilitySelector } from "src/reducers/project/capabilities";
import { useAppSelector } from "Store/hooks";

import { FormConfigType } from "./type";

import type { Integration } from "@packages/client";
import type { ThirdParty } from "Constants/integrationThirdParties";

const sysLogForm: FormConfigType[] = [
  {
    label: "Host",
    placeholder: "Enter Host",
    required: true,
    id: "host",
    inputType: "default"
  },
  {
    label: "Port Number",
    placeholder: "Enter port Number",
    required: true,
    id: "port",
    inputType: "default",
    type: "number"
  },
  {
    label: "Protocol",
    placeholder: "Choose Protocol",
    required: true,
    id: "protocol",
    inputType: "radio",
    options: [
      {
        id: "tls",
        label: "TLS",
        value: "tls"
      },
      {
        id: "tcp",
        label: "TCP",
        value: "tcp"
      },
      {
        id: "udp",
        label: "UDP",
        value: "udp"
      }
    ]
  },
  {
    label: "Facility",
    placeholder: "Enter Facility 0 - 29",
    id: "facility",
    inputType: "default",
    type: "number"
  },
  {
    label: "Message Format",
    required: true,
    id: "message_format",
    inputType: "radio",
    options: [
      {
        id: "rfc5424",
        label: "RFC5424",
        value: "rfc5424"
      },
      {
        id: "rfc3164",
        label: "RFC3164",
        value: "rfc3164"
      }
    ]
  },
  {
    label: "Auth Token",
    placeholder: "Enter your token",
    id: "auth_token",
    inputType: "textarea",
    height: 88,
    required: true
  },
  {
    label: "Auth mode",
    id: "auth_mode",
    inputType: "radio",
    options: [
      {
        id: "prefix",
        label: "Prefix",
        value: "prefix"
      },
      {
        label: "Structured data",
        value: "structured_data"
      }
    ]
  },
  {
    label: "Verify TLS certificate",
    placeholder: "Enter certificate bundle",
    id: "tls_verify",
    inputType: "switch"
  }
];
export const sysLogFormConfig = sysLogForm.map(cfg => ({
  ...cfg,
  name: cfg.id
}));

//splunk configuration
const splunkForm: FormConfigType[] = [
  {
    label: "URL",
    placeholder: "Enter URL",
    required: true,
    id: "url",
    inputType: "default"
  },
  {
    label: "Index",
    placeholder: "Provide index",
    required: true,
    id: "index",
    inputType: "default"
  },
  {
    label: "Auth Token",
    placeholder: "Enter your token",
    id: "token",
    inputType: "textarea",
    height: 88,
    required: true
  },
  {
    label: "Source type",
    placeholder: "Provide source type",
    required: true,
    id: "sourcetype",
    inputType: "default"
  },
  {
    label: "Verify TLS certificate",
    placeholder: "Enter certificate bundle",
    id: "tls_verify",
    inputType: "switch"
  }
];
export const splunkFormConfig = splunkForm.map(cfg => ({
  ...cfg,
  name: cfg.id
}));

//httplog configuration
export const getHttpLogConfig = (preventDeleteKeyValue?: boolean) =>
  [
    {
      label: "HTTP endpoint",
      placeholder: "Enter URL",
      required: true,
      id: "url",
      inputType: "default"
    },
    {
      label: "Verify TLS certificate",
      id: "tls_verify",
      inputType: "switch"
    },
    {
      label: "Verify TLS certificate",
      id: "tls_verify",
      inputType: "separator"
    },
    {
      label: "Headers",
      id: "headers",
      inputType: "keyValue",
      extra: {
        addLabel: "Add header",
        preventDeleteKeyValue
      }
    }
  ].map(cfg => ({
    ...cfg,
    name: cfg.id
  })) as FormConfigType[];

// sumologic configuratioin
const sumoLogicForm: FormConfigType[] = [
  {
    label: "URL",
    placeholder: "Enter URL",
    required: true,
    id: "url",
    inputType: "default"
  },
  {
    label: "Category",
    placeholder: "Category Role",
    required: true,
    id: "category",
    inputType: "default"
  },
  {
    label: "Verify TLS certificate",
    placeholder: "Enter certificate bundle",
    id: "tls_verify",
    inputType: "switch"
  }
];
export const sumoLogicFormConfig = sumoLogicForm.map(cfg => ({
  ...cfg,
  inputType: cfg.inputType,
  name: cfg.id
}));

// newrelic configuration
const newRelicForm: FormConfigType[] = [
  {
    label: "URL",
    placeholder: "Enter URL",
    required: true,
    id: "url",
    inputType: "default"
  },
  {
    label: "License Key",
    placeholder: "Enter License key",
    required: true,
    id: "license_key",
    inputType: "textarea",
    height: 88
  },
  {
    label: "Verify TLS certificate",
    placeholder: "Enter certificate bundle",
    id: "tls_verify",
    inputType: "switch"
  }
];
export const newRelicFormConfig = newRelicForm.map(cfg => ({
  ...cfg,
  inputType: cfg.inputType,
  name: cfg.id
}));

export const useObservabilitySuiteIntegrationList = () => {
  const { projectId, organizationId } = useDecodedParams<{
    projectId: string;
    organizationId: string;
  }>();

  const integrations: ReturnType<typeof integrationsSelector> =
    useSelectorWithUrlParams(integrationsSelector);

  const blackfireIntegration = integrations?.find(i => i.type === "blackfire");

  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      organizationId,
      projectId
    })
  );

  const subscription = useAppSelector(state =>
    subscriptionSelector(state, {
      organizationId,
      projectId: project?.id,
      id: project?.getSubscriptionId()
    })
  );

  const observabilitySuitePurchased =
    useBlackFireSellableStatus(subscription).observabilitySuitePurchased;
  const capabilities = useSelectorWithUrlParams(projectCapabilitySelector);
  const projectIntegration = capabilities?.integrations?.config;
  const subscriptionIntegration =
    subscription?.project_options?.enforced?.capabilities?.integrations?.config;

  const getIntegrationEnabled = (integrationName: string = "") => {
    const integration =
      subscriptionIntegration?.[integrationName] ||
      projectIntegration?.[integrationName];

    const hasIntegration = integrationName && integration?.enabled;
    const blackfireEnabled =
      integrationName == "blackfire" && blackfireIntegration;
    return !!(
      blackfireEnabled ||
      hasIntegration ||
      observabilitySuitePurchased
    );
  };

  const defaults = (integrationName?: string) => {
    const enabled = getIntegrationEnabled(integrationName);
    return {
      enabled,
      info: enabled
        ? undefined
        : {
            description: "log_forwarding.observability_required_message",
            title: "integration.observability_suite_upgrade_title"
          },
      description: `integration.forward_logs_to`,
      actionLinks: enabled
        ? []
        : [
            {
              href: "links.platform.contact",
              title: "support_sla_link_text_contact"
            }
          ]
    };
  };

  const blackfireInfo = (integration?: Integration) => {
    const description = integration
      ? "blackfire.integration.enabled_message"
      : "blackfire.integration.upgrade_message";

    return {
      info: {
        description,
        title: integration
          ? "blackfire.card.cta.launch"
          : "blackfire.integration.upgrade_title"
      },
      actionLinks: integration
        ? [
            {
              href: "links.blackfire.connect",
              title: "blackfire.card.cta.launch"
            }
          ]
        : [
            { href: "links.blackfire.pricing", title: "blackfire.add" },
            {
              href: "links.platform.contact",
              title: "support_sla_link_text_contact"
            }
          ],
      description
    };
  };

  const integrationConfig: ThirdParty[] = [
    {
      type: "syslog",
      category: "observability_suite",
      ...defaults("syslog")
    },
    {
      type: "splunk",
      category: "observability_suite",
      ...defaults("splunk")
    },
    {
      type: "sumologic",
      category: "observability_suite",
      ...defaults("sumologic")
    },
    {
      type: "newrelic",
      category: "observability_suite",
      ...defaults("newrelic")
    },
    {
      type: "httplog",
      category: "observability_suite",
      ...defaults("httplog")
    },
    {
      type: "blackfire",
      category: "monitoring",
      ...blackfireInfo(blackfireIntegration)
    }
  ];

  return {
    integrationConfig,
    isBlackFireEnabled: blackfireIntegration,
    isObserbilitySuiteEnabled: observabilitySuitePurchased,
    getIntegrationEnabled
  };
};
